import React, { useEffect, useState } from 'react';

import {
  Dialog,
  Text,
  Button,
  Stack,
  Inline,
  Divider,
  Heading,
  Columns,
  Column,
  ChevronLink,
  Box,
} from '@origin-digital/ods-core';
import { IconError, IconHome } from '@origin-digital/ods-icons';
import { chatMessengerShow } from '@origin-digital/event-dispatcher';
import { ConnectionAddressObject } from '@origin-digital/address-lookup';

import { ConfirmAddressListing } from './ConfirmAddressListing';
import { TNMIAddress } from './types';

import { useNMIResolution } from './useNMIResolution';

export interface IAddressConfirmation {
  addresses: TNMIAddress[];
  open: boolean;
  onClose: () => void;
  onConfirm: (nmi: string) => void;
  submitting: boolean;
  shouldShowChat?: boolean;
  onEnterAddressManually?: () => void;
  onEnterNmiManually?: () => void;
}

export const ConfirmAddressDialog: React.FC<IAddressConfirmation> = ({
  addresses,
  onClose,
  onConfirm,
  open = false,
  submitting,
  shouldShowChat = true,
  onEnterAddressManually,
  onEnterNmiManually,
}) => {
  const [selectedAddress, setSelectedAddress] = useState<string>();
  const [isError, setError] = useState<boolean>(false);

  const { maskNMILabel } = useNMIResolution();

  const singleAddress: ConnectionAddressObject | null =
    addresses.length === 1 ? addresses[0] : null;

  useEffect(() => {
    // Ensure that in the case of a single address, it is pre-selected for the user.
    if (singleAddress) {
      setSelectedAddress(singleAddress.nmi);
    }
  }, [singleAddress]);

  return (
    <Dialog
      open={open}
      title="Confirm your address"
      onClose={() => {
        setError(false);
        onClose();
      }}
      id="address-confirmation-dialog"
      data-id="address-confirmation-dialog"
      noFullScreenOnMobile
    >
      <Stack space="xlarge">
        <Stack space="medium">
          <Text>
            We just need to check we've got the right meter. It’s important we have the right one
            for your supply to be set up correctly.
          </Text>
          <Text>
            <Text inline={true} weight="medium">
              Top tip:
            </Text>{' '}
            you can match using the NMI (National Meter Identifier) found on the property's
            electricity bill.
          </Text>
          {singleAddress ? (
            <Box marginTop="medium" marginBottom="xxlarge">
              <Columns space="small">
                <Column width="content">
                  <IconHome />
                </Column>
                <Column>
                  <Stack space="xxsmall">
                    <Text>{singleAddress.addressLabel}</Text>
                    <Text variant="body-small" tone="neutralLight">
                      {maskNMILabel(singleAddress.nmi)}
                    </Text>
                  </Stack>
                </Column>
              </Columns>
            </Box>
          ) : (
            <ConfirmAddressListing
              id="address-confirmation-address-listing"
              addresses={addresses}
              onChange={(_, value) => {
                setError(false);
                setSelectedAddress(value);
              }}
              selectedAddress={selectedAddress}
              isError={isError}
            />
          )}
          {isError && (
            <Inline alignY="center" space="xxsmall">
              <IconError color="critical" size="inherit" />
              <Text variant="body-small" tone="critical">
                Please select an address.
              </Text>
            </Inline>
          )}
        </Stack>
        <Button
          aria-label="Confirm"
          spinner={submitting}
          onClick={() => {
            if (!selectedAddress) {
              return setError(true);
            } else {
              setError(false);
              onConfirm(selectedAddress);
            }
          }}
        >
          {singleAddress ? 'Yes, this is my address' : 'Confirm my address'}
        </Button>
        <Divider />
        {onEnterAddressManually && (
          <Stack space="medium">
            <Heading variant="h4">I can't see my address</Heading>
            <Text>
              Your address may not be entered correctly in our system. Please try a manual search,
              by entering your exact address.
            </Text>
            <Columns space="large">
              <Column>
                <ChevronLink
                  href={''}
                  color="primary"
                  onClick={(event) => {
                    event.preventDefault();
                    onEnterAddressManually();
                  }}
                >
                  Enter address manually
                </ChevronLink>
              </Column>
              {onEnterNmiManually ? (
                <Column>
                  <ChevronLink
                    href={''}
                    color="primary"
                    onClick={(event) => {
                      event.preventDefault();
                      onEnterNmiManually();
                    }}
                  >
                    Enter NMI manually
                  </ChevronLink>
                </Column>
              ) : null}
              <Divider />
            </Columns>
          </Stack>
        )}
        <Stack space="medium">
          <Heading variant="h4">
            {onEnterAddressManually ? `Still can't see your address?` : "I can't see my address"}
          </Heading>
          <Text>Let us help. Our team’s around from Monday to Friday, 8am-6pm.</Text>
          <Columns space="medium" collapseBelow="sm">
            {shouldShowChat ? (
              <Column>
                <Stack space="xxsmall">
                  <ChevronLink
                    href={''}
                    color="primary"
                    onClick={(event) => {
                      event.preventDefault();
                      chatMessengerShow({});
                    }}
                  >
                    Chat to us
                  </ChevronLink>
                </Stack>
              </Column>
            ) : null}
            <Column>
              <Stack space="xxsmall">
                <ChevronLink color="primary" href="tel:132461">
                  Call 13 24 61
                </ChevronLink>
              </Stack>
            </Column>
          </Columns>
        </Stack>
      </Stack>
    </Dialog>
  );
};
